/**
 * Domain types for Request Correspondence (#1069).
 *
 * Types only. Behaviour that used to sit alongside them moved out: role
 * classification to `team.ts`, where the rest of the membership logic lives,
 * and timestamp formatting to `format.ts`. The one exception is
 * `isMentionNotAllowedBody` (#1599 Story 1.4), the type guard of the wire
 * type beside it.
 */

/** Which audience a thread belongs to. Part of the container identity. */
export type SpaceId = 'internal' | 'shared';

export interface Message {
  id: string;
  threadId: string;
  /**
   * The author's numeric `users.user_id`, sent as a string (Story 1069-1.1,
   * AR3). Never an email and never derived from one. Compared with
   * `viewer.id` (the session's `User.id`, the same number) to decide "mine".
   *
   * ⚠️ NOT resolvable to a display name on the client: team members are keyed
   * by email here. Story 1.2's read endpoints supply the author's name.
   */
  authorId: string;
  /**
   * The author's display name, resolved server-side (Story 1069-1.2, AC 21).
   * `MessageRow` renders this; the id above is for identity only and must
   * never be shown as a name.
   */
  authorName: string;
  /**
   * Whether the author is Vertiv-internal, decided by the server's one
   * classifier (NFR26). Drives the "Customer" marker when the VIEWER may see
   * the internal space; never derived from a client team lookup.
   */
  authorIsInternal: boolean;
  /**
   * Present ONLY when the author is OUTSIDE the request's membership
   * projection (no team row, not the requestor): the label of the app role
   * that let them read, "Admin", "Supervisor", "Business Owner", ... Decided
   * by the server (`correspondenceAuthorRole.ts`); the client labels nobody.
   * The strip lists such a person only while a conversation they posted in is
   * open. Absent for every team member and whenever the projection did not
   * resolve, which lists fewer people rather than more.
   */
  authorNonMemberRole?: string;
  /**
   * The author wrote as the Vertiv Config Team (#1599 Story 4.1): the stored
   * bit, already zeroed by the server while `FEATURE_CONFIG_TEAM_IDENTITY` is
   * off, so the client renders on `=== true` and reads no flag. Optional here
   * because an older server or a cached page omits it: ABSENT READS AS FALSE.
   * Built into labels only through `authorLabel.ts`.
   */
  authorConfigTeam?: boolean;
  /** Empty string on a tombstone (`deleted: true`). */
  body: string;
  sentAt: string;
  /** Present only when the message has actually been edited (C4). */
  editedAt?: string;
  /**
   * Present, and `true`, only on a withdrawn message: author and `sentAt`
   * kept, `body` empty, no `editedAt`. Rendered since Story 5.5 (D8) as one
   * line between hairlines, `{author} withdrew a message · {time}`, and no
   * longer as a "Message withdrawn" bubble.
   */
  deleted?: true;
  /**
   * The files on this message (#1069 Story 4.2, AC 5).
   *
   * ⚠️ PINNED KEY-FOR-KEY WITH THE SERVER'S `Message` in
   * `server/services/correspondence.types.ts`, like every other key on this
   * type. This replaces `attachment?: { name, sizeLabel }` — a singular
   * placeholder no server code ever produced.
   *
   * ⚠️ ALREADY SORTED, IMAGES FIRST. The server orders them (AC 5) so the grid
   * cannot fragment into orphan rows; render the order given and do not
   * re-sort here, or the two tiers disagree about what the user saw.
   */
  attachments?: MessageFile[];
  /**
   * The people this message names (#1599 Story 1.4): mirror of
   * `server/services/correspondence.types.ts` `Message.mentions`.
   *
   * ⚠️ ALWAYS PRESENT, `[]` WHEN NOBODY IS NAMED: the one exception to this
   * type's "absent when empty" rule (Story 1.2). `messageDisplay` resolves
   * every token in the body against it on every render, so a token whose id
   * is here reads as the person's CURRENT name and one whose id is not (a
   * departed person, a self-mention) reads as its stored text.
   */
  mentions: MessageMention[];
}

/** One person a message names: mirror of `server/services/correspondence.types.ts`. */
export interface MessageMention {
  /** `users.user_id` as a string, like `authorId`. */
  userId: string;
  name: string;
}

/** One row of the @ suggester: mirror of `server/services/correspondence.types.ts`. */
export interface MentionablePerson {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
}

/**
 * The one collective mention (#1599 Story 1.7, FR-14): the group literal the
 * editor serialises as `data-mention-group` and the view rewrites, and the
 * text the view draws for it. Mirror of `server/services/configTeamIdentity.ts`
 * (`CONFIG_TEAM_GROUP`, `CONFIG_TEAM_LABEL`), duplicated under the house
 * workaround and pinned equal in `messageHtml.test.ts` beside the
 * `MENTION_SPAN_ATTRIBUTES` mirror pin.
 */
export const CONFIG_TEAM_GROUP = 'config-team' as const;
export const CONFIG_TEAM_LABEL = 'Vertiv Config Team';

/**
 * The mentionable endpoint's reply: mirror of
 * `server/services/correspondence.types.ts`. Since Story 1.7 the server sends
 * `group` whenever the projection resolved, in both spaces at every stage;
 * the list renders the team row only when present.
 */
export interface MentionableResponse {
  readonly space: SpaceId;
  readonly people: readonly MentionablePerson[];
  readonly group?: {
    readonly id: typeof CONFIG_TEAM_GROUP;
    readonly name: string;
  };
}

/** `400 MENTION_NOT_ALLOWED`'s body: mirror of `server/services/correspondence.types.ts`. */
export interface MentionNotAllowedBody {
  error: string;
  code: 'MENTION_NOT_ALLOWED';
  refused: string[];
  group?: true;
}

/**
 * Is this a `MENTION_NOT_ALLOWED` body? A shape check on a parsed error body
 * the client did not build: compared field by field, never looked up, so a
 * prototype-named key cannot satisfy it (`correspondence.md`, "Stored is
 * served").
 */
export const isMentionNotAllowedBody = (
  value: unknown,
): value is MentionNotAllowedBody => {
  if (typeof value !== 'object' || value === null) return false;
  if (!Object.prototype.hasOwnProperty.call(value, 'code')) return false;
  const { code, refused } = value as { code?: unknown; refused?: unknown };
  return (
    code === 'MENTION_NOT_ALLOWED' &&
    Array.isArray(refused) &&
    refused.every(id => typeof id === 'string')
  );
};

/**
 * One file on a message (#1069 Story 4.2). Pinned key-for-key with the server's
 * `MessageFile`.
 *
 * ⚠️ NO URL AND NO BYTES. The full file is fetched from the thread-scoped
 * serving route, whose URL the client builds from ids it already holds
 * (`messageFileUrl`); `thumbnailUri` is the small inline copy the grid draws.
 */
export interface MessageFile {
  id: string;
  /** As stored. Untrusted text: rendered, never executed. */
  name: string;
  /**
   * Tile or chip (AC 6, AC 8) — derived server-side from the SNIFFED type, so
   * a PDF claiming `image/png` is a document and a photo claiming
   * `application/octet-stream` is an image.
   */
  kind: 'image' | 'document';
  mimeType: string;
  /** DECODED bytes. Format with `formatFileSize`, the house function. */
  sizeBytes: number;
  /** `data:` URI, images only, absent when the server made no thumbnail. */
  thumbnailUri?: string;
}

/**
 * One file in a conversation's file listing (#1069 Story 7.4, FR77). Pinned
 * key-for-key with the server's `ThreadFile`.
 *
 * ⚠️ IT **EXTENDS** `MessageFile`, so the strip and the bubble draw the same
 * file from the same fields through the same pieces (`attachmentPieces.tsx`).
 * A field that meant something slightly different here would be a bug visible
 * only by comparing two surfaces.
 *
 * ⚠️ NO `authorIsInternal`. The strip draws no Customer marker; Story 7.6
 * lands the reader on the MESSAGE, which has one.
 */
export interface ThreadFile extends MessageFile {
  /** Which message this file was posted on. Story 7.6 jumps to it. */
  messageId: string;
  /** The message's send time, ISO. A file has no separate one. */
  sentAt: string;
  /** `users.user_id` of the message's author, as a string (AR3). */
  authorId: string;
  /** Resolved server-side. Never a number. */
  authorName: string;
  /**
   * #1599 Story 4.1, see `Message.authorConfigTeam`; absent reads as false.
   * The strip shows no visible author, so this reaches the row's spoken label.
   */
  authorConfigTeam?: boolean;
}

/**
 * The listing envelope (#1069 Story 7.4).
 *
 * ⚠️ NOT A PAGE. There is no cursor: the set is bounded by the conversation's
 * messages and fetched whole, bounded by `MAX_THREAD_FILE_LISTING`. A shape
 * that looked pageable would invite a caller to page it.
 *
 * ⚠️ `truncated` IS ABSENT, NEVER `false` — the rule `deleted` and `editedAt`
 * follow on `Message`.
 */
export interface ThreadFileListing {
  items: ThreadFile[];
  /** Present and `true` only when the server had more than the ceiling. */
  truncated?: true;
}

/**
 * A message the viewer has sent that the server has not yet confirmed
 * (Story 1069-1.3, AC 17). CLIENT STATE, NOT A WIRE SHAPE: it is deliberately
 * not a `Message` (both copies of that type are pinned key-for-key across the
 * tiers) and carries no author name from the server. `MessageList` renders it
 * after the data rows as an own-side row in a muted style; on the server's
 * reply it is replaced by the real `Message`, on failure it disappears and the
 * draft is still in the composer.
 */
export interface PendingMessage {
  /** Client-minted, never sent. */
  localId: string;
  threadId: string;
  /** The text as typed; the server's cleaned copy replaces it. */
  body: string;
  sentAt: string;
  /**
   * How many files ride on this post (#1069 Story 4.1, AC 8).
   *
   * ⚠️ ABSENT, NOT ZERO, FOR A TEXT-ONLY POST. AC 8 says the second line
   * renders "only when the post carries files" and that a text-only pending row
   * is untouched, so the row branches on the key being there rather than on a
   * count that happens to be 0.
   */
  fileCount?: number;
  /**
   * Upload progress, 0-100.
   *
   * ⚠️ `null` MEANS THE TRANSPORT CANNOT MEASURE THIS UPLOAD, and it is NOT
   * zero. AC 9: "Never animate a fake bar" — `null` draws the indeterminate
   * spinner, a number draws the bar, and absent means there is no upload to
   * report on.
   */
  percent?: number | null;
  /** Has this been in flight long enough to say so (AC 10)? */
  slow?: boolean;
}

/**
 * FR54's answer for one request, per space (#1069 P3.2).
 *
 * ⚠️ IT EXISTS BECAUSE THE PANEL DOES **NOT** HOLD BOTH SPACES' THREADS, WHICH
 * THE SPEC ASSUMED IT DID (measured 2026-08-25). The panel's thread query is
 * keyed `['correspondence', 'threads', requestId, space, viewerId]` (Story 6.3;
 * `${requestId}|${space}|${memberKey}` before it) and loads the ACTIVE space only,
 * so the inactive tab's threads are not in hand and never have been. Rather
 * than double the panel's fan-out to the messaging backend, the per-space
 * answer rides the summary endpoint, which reads our own mirror in one query.
 *
 * `undefined` means nobody asked. It renders as no dots, which is right: the
 * tab dot exists to STEER, and a guess is worse than silence.
 */
export interface SpaceUnread {
  internal?: boolean;
  shared?: boolean;
}

/**
 * The per-space CLOCKS off the FR54 summary (Story 1069-2.3, D1; FR69):
 * when anything in that audience last moved for this caller. `undefined`
 * when the summary has not arrived or carries no breakdown, `null` when the
 * audience has nothing kept for the caller, an ISO string otherwise. The
 * panel re-lists the audience it is showing when its string changes; it
 * never reads the merged unread for that. Plain strings, so the panel's
 * effect keys on primitives (the ~2,600-request loop of 2026-08 was an
 * object in a dependency list).
 */
export interface SpaceActivity {
  internal?: string | null;
  shared?: string | null;
}

export interface Thread {
  id: string;
  space: SpaceId;
  title: string;
  /**
   * The starter's numeric `users.user_id`, sent as a string (Story 1069-1.1,
   * AR3). Never an email. Not resolvable to a name on the client; Story 1.2's
   * read endpoints supply `createdByName`.
   */
  createdBy: string;
  /** The starter's display name, resolved server-side (Story 1069-1.2). */
  createdByName: string;
  createdAt: string;
  /**
   * When anything in this conversation last MOVED (Story 1069-2.3, D2; FR69):
   * the server's `last_activity_at`, always present. Moves on a post, an edit
   * and a withdrawal, and is not pulled back by the withdrawal, which is what
   * lets the panel tell "the conversation I am reading changed" from a
   * listing alone: `lastMessageAt` below is blind to an edit and moves BACK
   * on a withdrawal. (Only a skewed app-server clock moves it backwards; the
   * write does not guard against that.)
   */
  lastActivityAt: string;
  /**
   * Optional list-preview data, supplied by the provider when it is cheap to
   * obtain. Left undefined rather than faked when a backend does not return it,
   * so the list degrades honestly instead of claiming "no messages yet".
   */
  messageCount?: number;
  /** Plain-text preview (≤140 chars) of the latest LIVE message. */
  lastMessageBody?: string;
  /**
   * #1599 (owner quick-dev 2026-10-26), mirroring the server's
   * `Thread.lastMessageOwnMentions`: where THIS viewer's own @mention tokens
   * fall in `lastMessageBody`, as `[start, end)` ranges in CODE POINTS. The
   * row draws them in the conversation's own-token look and nothing else.
   * ABSENT READS AS NONE (an older server, an offline cache from before the
   * key, or nothing of the viewer's in the preview), and so do malformed or
   * out-of-range ranges: a plain preview, never a wrong highlight.
   */
  lastMessageOwnMentions?: Array<{ start: number; end: number }>;
  /**
   * How many files the latest LIVE message carries, `0` when it carries none
   * and absent when there is no live message.
   *
   * ⚠️ THE ROW'S PREVIEW NEEDS THREE CASES, NOT TWO. A message with files and
   * an empty body has no `lastMessageBody`, so a preview that branched on the
   * body alone reported "No messages" at a conversation someone had just
   * posted a file into. `No messages` means something specific and different —
   * every message was withdrawn — and this is what keeps the two apart.
   */
  lastMessageFileCount?: number;
  /** Numeric `users.user_id` as a string (AR3). See `createdBy`. */
  lastMessageAuthorId?: string;
  /** Display name of the latest live message's author. */
  lastMessageAuthorName?: string;
  /**
   * #1599 Story 4.1: the latest live message's author wrote as the Vertiv
   * Config Team. Present with `lastMessageAuthorId`; absent reads as false.
   */
  lastMessageAuthorConfigTeam?: boolean;
  /**
   * When the latest LIVE message was sent; absent when no live message exists
   * (Story 1069-1.2). Not the server's AR5 unread clock, which also moves on a
   * message that was later withdrawn: a row can be `unread` while this field
   * names an earlier message.
   */
  lastMessageAt?: string;
  /** Who can see this thread, when the backend reports it. */
  memberIds?: string[];
  /**
   * FR55: has this thread got something for THIS caller (#1069 P3.2)?
   *
   * ⚠️ THREE STATES, AND `undefined` IS NOT `false`. Since Story 1069-1.2 the
   * listing computes `unread` for EVERYONE who may read the audience, oversight
   * readers included, and since Story 1069-2.1 the summary's `hasUnread` (the
   * toggle dot, the inactive tab's badge) answers the same rule (FR52), so the
   * two never disagree about who is waited on. `undefined` therefore means ONE
   * thing: the server's read-state lookup FAILED for this listing. `false`
   * means "computed, and there is nothing". Collapsing the two with `?? false`
   * loses the distinction; collapsing them with `!!` at the render site is
   * harmless only because both render as NO DOT, which is exactly why the
   * difference has to be kept in the type rather than discovered later.
   *
   * ⚠️ THERE IS NO COUNT, AND ADDING ONE IS A PRODUCT DECISION. The owner
   * withdrew every count on 2026-08-25 (PRD r8): the signal is a dot, present
   * or absent. `messageCount` above is the thread's TOTAL and is deliberately
   * no longer rendered on the thread row — he read that integer in the card's
   * top-right corner as an unread count and reported working behaviour as a
   * bug, which is the finding that put the dot in that slot.
   */
  unread?: boolean;
  /**
   * FR-4 (#1599 Story 1.5), mirroring the server's `Thread.mentionsViewer`:
   * this thread mentions THIS viewer more recently than their read marker.
   * Three states as `unread`: `true`, `false` (computed, nothing), ABSENT
   * (the server's mentions flag is off or its lookup failed). The panel draws
   * the red `@` on `=== true` only and reads no flag of its own; opening the
   * thread clears it locally with `unread` (`useCorrespondence`).
   */
  mentionsViewer?: boolean;
  /**
   * FR74 (#1069 Story 7.3): may THIS caller write to this conversation?
   *
   * ⚠️ IT IS THE CALLER'S FACT, NOT THE ROW'S. The server computes it ONCE per
   * listing — `mayWriteSpace(caller, projection, space, region) &&
   * !caller.readOnly`, the same predicate the write gate runs — and copies it
   * onto every row, the way `unread` sits on the row for a per-caller fact.
   *
   * ⚠️ SAME PREDICATE IS NOT "CANNOT BE REFUSED" (review patch P7). This answer
   * is as old as the listing that carried it; the gate re-resolves the claim,
   * the entitlement and a fresh projection when the rename arrives. So a pencil
   * can be stale, and a rename behind a stale pencil comes back as the ordinary
   * write refusal the panel already renders (D8's line). The pencil is a
   * courtesy, never a permission.
   *
   * ⚠️ `undefined` IS WHAT AN ABSENT KEY READS AS -- the server never sends the
   * literal, because `JSON.stringify` drops an undefined property -- and IT
   * FAILS CLOSED. The rename pencil
   * renders only on `true`; `false` and `undefined` both withhold it. A
   * provider that does not answer this question therefore shows no pencil,
   * which is the safe direction.
   *
   * ⚠️ IT DOES NOT GATE THE COMPOSER. FR30 and Story 1.3 C7 decided the
   * composer renders on every request and a refusal is the server's to make;
   * this flag gates the pencil only. Reversing that is a product decision the
   * owner has not been asked — see `deferred-work.md`, "Deferred from:
   * 1069-7-3".
   */
  mayWrite?: boolean;
}
